import { Alert, Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { myAppointmentsLink } from '../../../library/constants';
import { toDateTimeInputValue } from '../../../library/dateTimeHelper';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import {
    CreateAppointmentRequest,
    EmptySlotResponse,
    IAppointment,
    REQUEST_CONFIRMATION_LEAD_HOURS,
} from '../../../library/models/appointment';
import { localised } from '../../../library/models/localisedText';
import { IBookingRequestSubmission, IPublicTherapist } from '../../../library/models/publicBooking';
import { IService } from '../../../library/models/service';
import { splitApiError } from '../../../library/useApiFormError';
import { useRefreshDiary } from '../../../library/useRefreshDiary';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { PersonLabel } from '../../userAvatar/UserAvatar';
import { SlotPicker } from '../AppointmentBookingForm/SlotPicker';
import { earliestBookable, isBookableFrom } from '../AppointmentBookingForm/slotGrouping';
import { clearDraft, readDraft, saveDraft } from './bookingDraft';
import { CheckEmail } from './CheckEmail';
import { createPublicBookingChoiceSchema, NOTE_MAX, PublicBookingChoiceValues } from './publicBookingSchema';
import {
    confirmRowStyles,
    emptyStateStyles,
    headerBoxStyles,
    loadingBoxStyles,
    noteFieldStyles,
    notReservedHintStyles,
    requestHintStyles,
    selectFieldStyles,
    stepBoxStyles,
    stepTitleStyles,
    subtitleStyles,
    titleStyles,
} from './publicBookingWizard.styles';
import { publicSlotSource } from './publicSlotSource';
import { FinishStep } from './steps/FinishStep';

/**
 * The choosing half of online booking: service, therapist, time, and only then who is asking.
 *
 * The same shape as the signed-in booking page -- steps that appear one after another on one
 * page, reading the same lists through their public equivalents -- so a patient who later books
 * from their account meets a page they already know.
 *
 * It serves two people. A **signed-out visitor** ends at `FinishStep`, which either sends the
 * request for confirmation by email or signs an existing patient in. A **signed-in patient** is
 * only ever here mid-flow -- having just signed in at that step, or come back from a link whose
 * time was taken -- and ends at the ordinary `POST /appointments` every patient booking uses.
 *
 * The choices are kept in a tab-scoped draft as they are made (see `bookingDraft`), so none of
 * those detours costs the visitor what they had already picked.
 */
export const PublicBookingWizard = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hostname, hostContext } = useBranding();
    const { isAuthenticated } = useAuth();
    const { showSnackbar } = useSnackbar();
    const refreshDiary = useRefreshDiary();

    const schema = useMemo(() => createPublicBookingChoiceSchema(translate), [translate]);

    /*
     * Read once. A restored slot is kept only while it is still bookable: a draft written an
     * hour ago may name a time that has since passed the four hours' notice.
     */
    const [draft] = useState(() => readDraft(hostname));
    const restoredSlot: EmptySlotResponse | null =
        draft?.slot && isBookableFrom(draft.slot.startTime, earliestBookable(toDateTimeInputValue(new Date())))
            ? draft.slot
            : null;
    const linkedServiceId: string = searchParams.get('serviceId') ?? '';

    const { register, setValue, watch, formState: { errors, isValid } } =
        useValidatedForm<PublicBookingChoiceValues>(schema, {
            defaultValues: {
                serviceId: draft ? String(draft.serviceId) : linkedServiceId,
                therapistId: draft?.therapistId ? String(draft.therapistId) : '',
                slotStartsAt: restoredSlot?.startTime ?? '',
                note: draft?.note ?? '',
            },
        });

    const serviceId: string = watch('serviceId');
    const therapistId: string = watch('therapistId');
    const note: string = watch('note');

    const [slot, setSlot] = useState<EmptySlotResponse | null>(restoredSlot);

    /** The request just sent for confirmation, which the check-email screen can send again. */
    const [sent, setSent] = useState<IBookingRequestSubmission | null>(null);

    const { data: servicesList, isLoading: isLoadingServices } = useReactQuery<IService[]>(
        services.publicBooking.getServices(hostname)
    );

    const { data: therapists, isLoading: isLoadingTherapists } = useReactQuery<IPublicTherapist[]>(
        services.publicBooking.getTherapists(hostname, Number(serviceId)),
        { enabled: serviceId !== '' }
    );

    const chosenService: IService | undefined =
        servicesList?.find(option => String(option.id) === serviceId);

    // Written as each choice is made, so a reload or a sign-in keeps them. Nothing the visitor
    // types about themselves goes in -- see bookingDraft.
    useEffect(() => {
        if (serviceId !== '') {
            saveDraft(hostname, {
                serviceId: Number(serviceId),
                therapistId: therapistId !== '' ? Number(therapistId) : null,
                slot,
                note,
            });
        }
    }, [hostname, serviceId, therapistId, slot, note]);

    const book = useReactMutation<IAppointment, Error, CreateAppointmentRequest>(
        services.appointments.createAppointmentMutation()
    );

    const onSelectSlot = (next: EmptySlotResponse | null): void => {
        setSlot(next);
        setValue('slotStartsAt', next?.startTime ?? '', { shouldValidate: true });
    };

    /**
     * The signed-in ending: exactly what the patient's own booking page sends. The API forces it
     * to a request and applies every rule a patient booking obeys.
     */
    const submitAsPatient = (): void => {
        if (!slot || !chosenService) {
            return;
        }

        book.mutate(
            {
                therapistId: Number(therapistId),
                serviceId: chosenService.id,
                startsAt: slot.startTime,
                endsAt: slot.endTime,
                notes: note.trim() || undefined,
            },
            {
                onSuccess: () => {
                    clearDraft(hostname);
                    refreshDiary();
                    showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_SENT), 'success');
                    navigate(myAppointmentsLink);
                },
                onError: (error: unknown) => {
                    // Most likely the hour went while they were signing in: say so in the API's
                    // own words and let them pick again from a strip that no longer offers it.
                    onSelectSlot(null);
                    showSnackbar(
                        splitApiError(error, [], translate, ResourcesIds.APPOINTMENT_BOOK_FAILED).formError
                        ?? translate(ResourcesIds.APPOINTMENT_BOOK_FAILED),
                        'error'
                    );
                },
            }
        );
    };

    if (sent) {
        return (
            <CheckEmail
                submission={sent}
                onResent={setSent}
                onChangeDetails={() => setSent(null)}
            />
        );
    }

    const serviceLabel = (option: IService): string =>
        `${localised(option.nameRo, option.nameEn, i18n.language)} · `
        + localised(option.durationLabelRo, option.durationLabelEn, i18n.language);

    return (
        <>
            <Box sx={headerBoxStyles}>
                <Typography variant="h4" component="h1" gutterBottom sx={titleStyles}>
                    {translate(ResourcesIds.PUBLIC_BOOKING_TITLE)}
                </Typography>
                <Typography sx={subtitleStyles}>
                    {translate(ResourcesIds.PUBLIC_BOOKING_SUBTITLE)}
                </Typography>
            </Box>

            <Box sx={stepBoxStyles}>
                <Typography sx={stepTitleStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT_STEP_SERVICE)}
                </Typography>

                {isLoadingServices ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (servicesList ?? []).length === 0 ? (
                    <Typography sx={emptyStateStyles}>
                        {translate(ResourcesIds.PUBLIC_BOOKING_NO_SERVICES)}
                    </Typography>
                ) : (
                    <TextField
                        select
                        fullWidth
                        size="small"
                        sx={selectFieldStyles}
                        label={translate(ResourcesIds.MY_CALENDAR_ADD_SERVICE)}
                        // A price-list link may name a service that is not bookable online; the
                        // select then starts empty rather than warning about a value it lacks.
                        value={chosenService ? serviceId : ''}
                        onChange={event => {
                            setValue('serviceId', event.target.value, { shouldValidate: true });
                            setValue('therapistId', '', { shouldValidate: true });
                            onSelectSlot(null);
                        }}
                    >
                        {(servicesList ?? []).map(option => (
                            <MenuItem key={option.id} value={String(option.id)}>
                                {serviceLabel(option)}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>

            {chosenService && (
                <Box sx={stepBoxStyles}>
                    <Typography sx={stepTitleStyles}>
                        {translate(ResourcesIds.BOOK_APPOINTMENT_STEP_THERAPIST)}
                    </Typography>

                    {isLoadingTherapists ? (
                        <Box sx={loadingBoxStyles}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (therapists ?? []).length === 0 ? (
                        <Typography sx={emptyStateStyles}>
                            {translate(ResourcesIds.BOOK_APPOINTMENT_NO_THERAPISTS)}
                        </Typography>
                    ) : (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            sx={selectFieldStyles}
                            label={translate(ResourcesIds.ADD_PURCHASE_THERAPIST_LABEL)}
                            value={(therapists ?? []).some(option => String(option.id) === therapistId)
                                ? therapistId
                                : ''}
                            onChange={event => {
                                setValue('therapistId', event.target.value, { shouldValidate: true });
                                onSelectSlot(null);
                            }}
                        >
                            {(therapists ?? []).map(option => (
                                <MenuItem key={option.id} value={String(option.id)}>
                                    <PersonLabel person={option} />
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>
            )}

            {chosenService && therapistId !== '' && (
                <Box sx={stepBoxStyles}>
                    <Typography sx={stepTitleStyles}>
                        {translate(ResourcesIds.BOOK_APPOINTMENT_STEP_SLOT)}
                    </Typography>

                    <SlotPicker
                        therapistId={Number(therapistId)}
                        durationMinutes={chosenService.duration}
                        selected={slot}
                        onSelect={onSelectSlot}
                        source={publicSlotSource(hostname, chosenService.id)}
                    />

                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        sx={noteFieldStyles}
                        label={translate(ResourcesIds.PUBLIC_BOOKING_NOTE_LABEL)}
                        slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
                        {...register('note')}
                        {...fieldProps(errors, 'note', translate(ResourcesIds.PUBLIC_BOOKING_NOTE_HINT))}
                    />
                </Box>
            )}

            {chosenService && therapistId !== '' && slot && (
                <Box sx={stepBoxStyles}>
                    <Typography sx={stepTitleStyles}>
                        {translate(ResourcesIds.PUBLIC_BOOKING_STEP_DETAILS)}
                    </Typography>

                    {isAuthenticated ? (
                        <>
                            <Typography sx={requestHintStyles}>
                                {translate(ResourcesIds.BOOK_APPOINTMENT_REQUEST_HINT, {
                                    hours: REQUEST_CONFIRMATION_LEAD_HOURS,
                                })}
                            </Typography>
                            <Box sx={confirmRowStyles}>
                                <Button
                                    variant="contained"
                                    disabled={!isValid || book.isPending}
                                    onClick={submitAsPatient}
                                >
                                    {translate(ResourcesIds.BOOK_APPOINTMENT_CONFIRM)}
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Alert severity="info" sx={notReservedHintStyles}>
                                {translate(ResourcesIds.PUBLIC_BOOKING_NOT_RESERVED_HINT)}
                            </Alert>
                            <FinishStep
                                clinicName={hostContext?.displayName ?? ''}
                                booking={{
                                    serviceId: chosenService.id,
                                    therapistId: Number(therapistId),
                                    startsAt: slot.startTime,
                                    note: note.trim() || undefined,
                                }}
                                onSent={setSent}
                                onSignedIn={submitAsPatient}
                            />
                        </>
                    )}
                </Box>
            )}
        </>
    );
};
